import { FontAwesome } from '@expo/vector-icons';
import { useIsFocused } from "expo-router/react-navigation";
import { Image } from 'expo-image';
import { router, useLocalSearchParams, useNavigation } from 'expo-router';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Pressable, StyleSheet, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import Animated, {
  Easing,
  ReduceMotion,
  ScrollEvent,
  useAnimatedReaction,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useGetAllEpisodesOfShow } from '@/api/hooks/showHooks';
import { ShowEpisode } from '@/api/models/showModels';
import GoBackButton from '@/components/buttons/GoBackButton';
import DotsLoader from '@/components/DotsLoader';
import Text from '@/components/Text';
import Title from '@/components/Title';
import EpisodeCard from '@/components/trbShow/EpisodeCard';
import EpisodeSelectedHeader from '@/components/trbShow/EpisodeSelectedHeader';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader';
import { AppStorage } from '@/utils/AppStorage';
import { firebaseImage, safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { getAllEpisodeProgress, getNextEpisodeId } from '@/utils/showEpisodesUtils';

const Show = () => {
  const navigation = useNavigation();
  const params = useLocalSearchParams();
  const isFocused = useIsFocused();

  const showId = typeof params.showId === 'string' ? params.showId : undefined;

  const { showError } = useEndFlowModal();
  const { height } = useWindowDimensions();

  const { data, isPending } = useGetAllEpisodesOfShow({ showId: showId ?? '' });

  const [selectedEpisode, setSelectedEpisode] = useState<ShowEpisode>();
  const [hasProgress, setHasProgress] = useState<boolean>(false);

  //Locales
  const title = i18n.t('mix.show_title');

  // Animation
  const MIN_HEADER_HEIGHT = 200;
  const EXTRA_HEIGHT = 100;
  const TIMING_CONFIG = {
    duration: 300,
    easing: Easing.inOut(Easing.quad),
    reduceMotion: ReduceMotion.System
  };
  const maxHeaderHeight = height * 0.4;

  // Shared values
  const scrollOffset = useSharedValue(0);
  const opacity = useSharedValue(1);
  const translateX = useSharedValue(0);
  const extraHeight = useSharedValue(EXTRA_HEIGHT);

  // Refs
  const animatedRef = useAnimatedRef<Animated.FlatList<ShowEpisode>>();

  // Header animation
  const { animatedHeight, showExtra } = useCollapsibleHeader({
    scrollOffset,
    maxHeaderHeight,
    minHeaderHeight: MIN_HEADER_HEIGHT
  });

  // Scroll handler
  const scrollHandler = useAnimatedScrollHandler((event: ScrollEvent) => {
    const offset = event.contentOffset.y;
    if (offset < maxHeaderHeight) {
      scrollOffset.value = offset;
    }
  });

  // Header style
  const animatedHeaderStyle = useAnimatedStyle(() => {
    return {
      height: Math.round(animatedHeight.value)
    };
  });

  // Extra section style
  const animatedExtraStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    height: extraHeight.value,
    transform: [{ translateX: translateX.value }]
  }));

  // Animation handlers
  const showExtraAnimation = () => {
    'worklet';
    extraHeight.value = withTiming(
      EXTRA_HEIGHT,
      { duration: 50, easing: Easing.inOut(Easing.quad), reduceMotion: ReduceMotion.System },
      (finished: boolean) => {
        if (finished) {
          if (opacity.value !== 1) {
            opacity.value = withTiming(1, { ...TIMING_CONFIG, duration: 750 });
          }
          if (translateX.value !== 0) {
            translateX.value = withTiming(0, TIMING_CONFIG);
          }
        }
      }
    );
  };

  const hideExtraAnimation = () => {
    'worklet';
    if (translateX.value !== 15) {
      translateX.value = withTiming(15, TIMING_CONFIG);
    }
    if (opacity.value !== 0) {
      opacity.value = withTiming(0, TIMING_CONFIG);
    }
    if (extraHeight.value !== 0) {
      extraHeight.value = withTiming(0, TIMING_CONFIG);
    }
  };

  useAnimatedReaction(
    () => showExtra.value,
    (current: boolean, previous: boolean | null) => {
      if (current !== previous) {
        if (current) {
          showExtraAnimation();
        } else {
          hideExtraAnimation();
        }
      }
    },
    []
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={title} colorVariant="alt" />,
      headerLeft: () => (
        <GoBackButton
          iconColor="white"
          onPress={() => {
            if (!router.canGoBack()) router.replace('/home/npoTabs');
            else router.back();
          }}
        />
      ),
      headerRight: () => (
        <TouchableOpacity onPress={() => router.navigate('/show/giftSeries')}>
          <View style={styles.giftContainer}>
            <FontAwesome name="gift" size={20} color="#FE9722" />
            <Text style={styles.giftText}>{i18n.t('mix.gift')}</Text>
          </View>
        </TouchableOpacity>
      )
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigation]);

  useEffect(() => {
    if (isFocused) {
      if (!isPending) {
        if (data?.length) {
          const loadLastWatched = async () => {
            const allProgress = await getAllEpisodeProgress();
            setHasProgress(Object.keys(allProgress).length > 0);

            const lastCompletedId = await AppStorage.getItem('lastEpisodeCompleted');
            const lastWatchedId = await AppStorage.getItem('lastEpisodeWatched');

            let selected: ShowEpisode | undefined;
            if (lastCompletedId) {
              const nextId = getNextEpisodeId(data ?? [], lastCompletedId);
              selected = data?.find((ep) => ep.id === nextId) ?? data?.find((ep) => ep.id === lastCompletedId);
            }

            if (!selected && lastWatchedId) {
              selected = data?.find((ep) => ep.id === lastWatchedId);
            }

            if (!selected) {
              selected = data?.[0];
            }

            setSelectedEpisode(selected);
          };
          loadLastWatched();
        } else {
          showError({ buttonLabel: i18n.t('mix.go_back'), onButtonPress: () => router.back() });
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending, isFocused]);

  // Unmount animated values
  useEffect(() => {
    return () => {
      scrollOffset.value = 0;
      opacity.value = 1;
      translateX.value = 0;
      extraHeight.value = EXTRA_HEIGHT;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isPending || !showId) {
    return (
      <View style={styles.mainContainer}>
        <DotsLoader />
      </View>
    );
  }

  return (
    <SafeAreaView edges={['bottom']} style={[styles.flexOne, styles.mainContainer]}>
      <View style={styles.mainContainer}>
        <Animated.View style={[styles.headerContainer, animatedHeaderStyle]}>
          <Pressable
            onPress={() => {
              router.push({
                pathname: '/show/[episodeId]',
                params: {
                  episodeId: selectedEpisode?.id ?? data?.at(0)?.id ?? ''
                }
              });
            }}
            style={{ ...styles.playPressable, height: maxHeaderHeight }}>
            <Image
              style={StyleSheet.absoluteFill}
              source={{ uri: firebaseImage(safeJsonParseArray(selectedEpisode?.thumbnail ?? '[]').at(0)) }}
              contentFit="cover"
            />
            <View style={styles.playIconContainer}>
              <FontAwesome name="play-circle" size={64} color="white" />
            </View>
          </Pressable>
        </Animated.View>
        <View style={styles.content}>
          {selectedEpisode && (
            <Animated.View style={[styles.extraContainer, animatedExtraStyle]}>
              <EpisodeSelectedHeader
                selectedEpisode={selectedEpisode}
                continueWatching={hasProgress}
                numberOfEpisodes={data?.length ?? 7}
                onPlay={() => {
                  router.push({
                    pathname: '/show/[episodeId]',
                    params: {
                      episodeId: selectedEpisode?.id ?? data?.at(0)?.id ?? ''
                    }
                  });
                }}
              />
            </Animated.View>
          )}

          <Animated.FlatList
            style={styles.episodeFlatList}
            contentContainerStyle={styles.episodesContainer}
            ref={animatedRef}
            bounces={false}
            scrollEventThrottle={16}
            onScroll={scrollHandler}
            data={data}
            renderItem={({ item }: { item: ShowEpisode }) => (
              <EpisodeCard
                key={item.id}
                item={item}
                onPress={() => {
                  AppStorage.setItem('lastEpisodeWatched', item.id);
                  AppStorage.deleteItem('lastEpisodeCompleted');
                  setSelectedEpisode(item);
                  router.push({
                    pathname: '/show/[episodeId]',
                    params: {
                      episodeId: item?.id ?? ''
                    }
                  });
                }}
              />
            )}
          />
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  flexOne: {
    flex: 1
  },

  mainContainer: {
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'black'
  },

  headerContainer: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'black',
    overflow: 'hidden'
  },

  extraContainer: {
    width: '100%',
    overflow: 'visible',
    zIndex: 10
  },

  container: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },

  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 25,
    paddingHorizontal: 18,
    width: '100%',
    height: '45%',
    gap: 15
  },

  episodeFlatList: {
    width: '100%'
  },

  episodesContainer: {
    paddingVertical: 10,
    gap: 27
  },

  playPressable: {
    position: 'relative',
    width: '100%'
  },

  playIconContainer: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -32 }, { translateY: -32 }], // center the 64px icon
    zIndex: 1
  },

  giftContainer: {
    gap: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10
  },

  giftText: {
    color: '#FE9722',
    fontSize: 10
  }
});

export default Show;
